//@ts-check
import { Browser } from '../utils/browsercheck';
import { slideDown, slideToggle, slideUp } from '../utils/effects';

/** @import { MenuOptions } from "../components/menu" */

export class DropdownMenu {
    header;

    /** The root element of the menu. */
    root;

    /** The burger menu */
    navButton;

    /** @type {{ element: HTMLElement, close: HTMLElement } | null} */
    overlay = null;

    /** The menu options */
    options;

    /** @type {Array<Submenu>} */
    submenus = [];

    /** @type {NodeListOf<HTMLAnchorElement> } AllLinks from menu - all */
    links;

    skipNav;

    /**
     * @param { MenuOptions } options
     */
    constructor(options) {
        this.options = options;
        this.root = this.#query(options.selector);
        this.header = this.root.closest('#header, #fixed-header');
        this.navButton = this.#query(options.navButton);
        this.links = this.#queryAll(`${options.selector} a`);
        this.skipNav = this.#query('a[href*="skipNavigation"]');

        if (options.overlay) {
            this.overlay = {
                element: this.#query(options.overlay.element),
                close: this.#query(options.overlay.close),
            };
        }

        this.#getSubmenus();
        this.#registerEvents();

        if (this.inMobileMode()) {
            this.openTrail();
        }
    }

    inMobileMode() {
        return window.innerWidth < this.options.breakPoint;
    }

    toggleMenu() {
        slideToggle(this.root, 0.5, () => {
            this.header?.classList.toggle('menu-open');
        });

        this.navButton.classList.toggle('active');
    }

    closeMenu() {
        if (this.header?.classList.contains('menu-open')) {
            this.toggleMenu();
        }
    }

    openTrail() {
        for (const submenu of this.submenus) {
            if (submenu.state.isTrail && !submenu.state.isOpen) {
                submenu.openLevel();
            }
        }
    }

    toggleOverlay() {
        if (this.overlay?.element.classList.contains('open')) {
            this.overlay.element.classList.replace('open', 'close');
            setTimeout(() => this.overlay?.element.classList.remove('close'), 300);
        } else {
            this.overlay?.element.classList.add('open');
        }
    }

    hasOpenLevels() {
        return this.submenus.some((submenu) => submenu.state.isOpen);
    }

    /**
     * @param {number} level
     */
    closeOpenLevels(level = 1) {
        for (const submenu of this.submenus) {
            if (submenu.state.isOpen && submenu.level >= level) {
                submenu.closeLevel();
            }
        }
    }

    /**
     * @param {string} selector
     *
     * @returns {HTMLElement}
     */
    #query(selector) {
        const element = document.querySelector(selector);

        if (!(element instanceof HTMLElement)) {
            throw new Error(`Could not find element with selector ${selector}`);
        }

        return element;
    }

    /**
     * @param {string} selector
     *
     * @returns {NodeListOf<HTMLAnchorElement>}
     */
    #queryAll(selector) {
        const element = document.querySelectorAll(selector);

        if (!(element instanceof NodeList)) {
            throw new Error(`Could not find element with selector ${selector}`);
        }

        /* @ts-ignore  */
        return element;
    }

    #getSubmenus() {
        for (const link of this.root.querySelectorAll('a.submenu, strong.submenu')) {
            this.submenus.push(new Submenu(link, this));
        }
    }

    #registerEvents() {
        if (this.overlay) {
            this.navButton.addEventListener('click', () => this.toggleOverlay());
            this.overlay.close.addEventListener('click', () => this.toggleOverlay());
        } else {
            this.navButton.addEventListener('click', () => this.toggleMenu());
        }

        if (Browser.isOnePage) {
            const links = this.root.querySelectorAll('a:not(.submenu)[href*="#"]');

            for (const link of links) {
                link.addEventListener('click', () => this.#onLinkClick());
            }
        }

        window.addEventListener('click', (e) => this.#onClickOutside(e));
        window.addEventListener('resize', () => this.#onResize());
        document.addEventListener('keyup', (e) => this.#onEscape(e));

        this.skipNav.addEventListener('click', () => this.inMobileMode() && this.toggleMenu());

        this.#onHide();
    }

    #onHide() {
        const fixedHeader = this.root.closest('#fixed-header');

        if (!fixedHeader) {
            return;
        }

        let watch = false;

        new MutationObserver((mutationList) => {
            for (const mutation of mutationList) {
                if (mutation.attributeName !== 'class') {
                    return;
                }

                if (watch && !fixedHeader.classList.contains('show')) {
                    this.inMobileMode() ? this.closeMenu() : this.closeOpenLevels();
                    watch = false;
                }

                if (fixedHeader.classList.contains('show')) {
                    watch = true;
                }
            }
        }).observe(fixedHeader, { attributes: true });
    }

    /**
     * @param {KeyboardEvent} event
     */
    #onEscape(event) {
        if (event.key === 'Escape' && !this.inMobileMode()) {
            this.closeOpenLevels();
        }
    }

    #onLinkClick() {
        // In case of one-page navigation, close the menu when a link is clicked
        if (this.inMobileMode()) this.toggleMenu();
    }

    #onResize() {
        this.inMobileMode() ? this.openTrail() : this.#resetMobileMenu();
    }

    /**
     * @param {MouseEvent} event
     */
    #onClickOutside(event) {
        if (this.inMobileMode()) {
            return;
        }

        if (
            event.target instanceof HTMLElement &&
            !this.root.contains(event.target) &&
            !this.navButton.contains(event.target)
        ) {
            this.closeOpenLevels();
        }
    }

    #resetMobileMenu() {
        this.#resetOpenLevels();
        this.root.style.display = '';
        this.navButton.classList.remove('active');
        this.header?.classList.remove('menu-open');
    }

    #resetOpenLevels() {
        for (const submenu of this.submenus) {
            if (submenu.state.isOpen) submenu.closeLevel(0);
        }
    }
}

class Submenu {
    /** The parent DropdownMenu instance. */
    parent;

    /** The link that opens the submenu (a) */
    link;

    /** The button that opens the submenu (button) */
    submenuBtn;

    /** The parent container of the link (li) */
    container;

    /** The submenu itself (ul) */
    menu;

    /** The duration of the slide animation */
    duration = 0.2;

    /** The level number of the submenu */
    level;

    state = {
        isOpen: false,
        isTrail: false,
    };

    /** @type {gsap.core.Tween|null} */
    animation = null;

    /**
     * @param {Element} link
     * @param {DropdownMenu} parent
     */
    constructor(link, parent) {
        if (!(link instanceof HTMLAnchorElement || link.tagName === 'STRONG')) {
            throw new Error('Invalid link element for the submenu');
        }

        const container = link.parentElement;
        const menu = link.nextElementSibling;

        if (!(container instanceof HTMLLIElement) || !(menu instanceof HTMLUListElement)) {
            throw new Error('Invalid parent elements for the submenu');
        }

        this.parent = parent;
        this.link = link;
        this.container = container;
        this.menu = menu;

        const match = this.menu.className.match(/level_(\d+)/);
        this.level = match ? Number.parseInt(match[1]) : 0;

        this.submenuBtn = this.#addButton();

        if (this.link.classList.contains('trail')) {
            this.state.isTrail = true;
        }

        this.#registerEvents();
    }

    openLevel() {
        this.menu.classList.remove('show-left');

        if (!this.parent.inMobileMode() && this.level > 2) {
            const containerRect = this.container.getBoundingClientRect();
            const availableSpace = window.innerWidth - containerRect.right;

            if (availableSpace < 300) {
                this.menu.classList.add('show-left');
            }
        }

        this.animation = slideDown(this.menu, this.duration);
        this.state.isOpen = true;
        this.submenuBtn.ariaExpanded = 'true';
        this.submenuBtn.ariaLabel = `Untermenü Level ${this.level} schließen`;
        this.link.classList.add('open');
        this.parent.header?.classList.add('level-open');
    }

    closeLevel(duration = this.duration) {
        this.animation = slideUp(this.menu, duration, () => {
            this.link.classList.remove('open');
        });

        this.state.isOpen = false;
        this.submenuBtn.ariaExpanded = 'false';
        this.submenuBtn.ariaLabel = `Untermenü Level ${this.level} öffnen`;

        if (!this.parent.hasOpenLevels()) {
            this.parent.header?.classList.remove('level-open');
        }
    }

    toggleLevel() {
        this.state.isOpen ? this.closeLevel() : this.openLevel();
    }

    #addButton() {
        const button = document.createElement('button');
        button.classList.add('toggle-submenu-btn');
        button.type = 'button';
        button.ariaLabel = `Untermenü Level ${this.level} öffnen`;
        button.ariaHasPopup = 'true';
        button.ariaExpanded = 'false';

        this.link.after(button);

        return button;
    }

    #registerEvents() {
        this.container.addEventListener('mouseenter', () => this.#onHover('enter'));
        this.container.addEventListener('mouseleave', () => this.#onHover('leave'));
        this.container.addEventListener('focusout', (e) => this.#onFocusOut(e));
        this.container.addEventListener('click', (e) => this.#onClick(e));
        this.submenuBtn.addEventListener('click', () => this.toggleLevel());
    }

    /**
     * @param {FocusEvent} event
     */
    #onFocusOut(event) {
        if (event.relatedTarget instanceof Node && this.state.isOpen && !this.container.contains(event.relatedTarget)) {
            this.closeLevel();
        }
    }

    /**
     * @param {'enter' | 'leave'} direction
     */
    #onHover(direction) {
        if (this.parent.inMobileMode() || Browser.isTouchDevice) {
            return;
        }

        direction === 'enter' ? this.openLevel() : this.closeLevel();
    }

    /**
     * @param {MouseEvent | PointerEvent} event
     */
    #onClick(event) {
        if (!(event instanceof PointerEvent) || event.pointerType !== 'touch') {
            return;
        }

        event.stopPropagation();

        if (event.target instanceof HTMLAnchorElement && !event.target.classList.contains('submenu')) {
            return;
        }

        event.preventDefault();

        if (this.state.isOpen) {
            this.closeLevel();
        } else {
            this.parent.closeOpenLevels(this.level);
            this.openLevel();
        }
    }
}
